import { useEffect, useState } from 'react';
import { Col, Input, Row, Select } from 'antd';
import {
  Fieldset,
  StyledModal,
  Form,
  CustomNotification,
  FlagCountrySelectionFieldInput,
} from 'common/components';
import CreateProductModalFooter from './CreateProductModalFooter';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import * as ssoProductSelectors from 'pages/sso-product/controllers/selectors';
import { createSupplier } from 'services/memberMaintenance';
import Messages from 'i18n/messages/message-my-company';
import './CreateSupplierModal.less';

const { Option } = Select;

const CreateSupplierModal = (props) => {
  const { visible, setVisible, setSupplierList, memberId } = props;

  const [loading, setLoading] = useState(false);
  const addProductForm = Form.useFormInstance();
  const [form] = Form.useForm();

  const {
    newSupplier,
    contactEmail,
    contactFirstName,
    contactLastName,
    contactPhoneNumber,
    supplierName,
  } = useSelector(ssoProductSelectors.selectBpSsoProductData()) ?? {};

  const handleCancel = () => {
    setVisible(false);
  };

  const handleCreateSupplier = async (params) => {
    setLoading(true);
    try {
      params.memberId = memberId;
      const { isSuccess, message, data } = await createSupplier({
        ...params,
        status: 'Active',
        Visibility: 'Public',
        supplierName: params.supplierName?.trim(),
      });
      if (isSuccess) {
        setVisible(false);
        form.resetFields();
        addProductForm.setFieldsValue({ supplierId: data?.supplierId });
        setSupplierList((pre) => [
          ...pre,
          {
            supplierId: data?.supplierId,
            supplierName: params.supplierName?.trim(),
          },
        ]);
        CustomNotification.success('Create supplier successfully');
      } else {
        CustomNotification.error(message ?? 'Something went wrong');
      }
    } catch (error) {
      CustomNotification.error(error.message ?? 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      handleCreateSupplier(values);
    });
  };

  // inside your CreateSupplierModal component

  useEffect(() => {
    if (!visible) {
      // when modal is closed -> clear form and errors
      form.resetFields();
    }
  }, [visible, form]);

  useEffect(() => {
    if (visible) {
      // wait for modal to render
      setTimeout(() => {
        const modalBody = document.querySelector(
          '.document-modal .ant-modal-body'
        );
        if (modalBody) {
          modalBody.scrollTop = 0;
        }
      }, 0);
    }
  }, [visible]);

  useEffect(() => {
    const supplierInfo =
      newSupplier === '1'
        ? {
            saleContactFirstName: contactFirstName,
            saleContactLastName: contactLastName,
            saleContactEmail: contactEmail,
            saleContactPhone: contactPhoneNumber,
            supplierName: supplierName,
          }
        : {};

    form.setFieldsValue({
      ...form.getFieldsValue(),
      ...supplierInfo,
      country: 'US',
    });
  }, []);

  const modalProps = {
    title: 'New Supplier',
    visible: visible,
    width: '1000px',
    closable: true,
    maskClosable: false,
    wrapClassName: 'document-modal',
    onCancel: handleCancel,
    footer: (
      <CreateProductModalFooter
        onCancel={handleCancel}
        onOk={handleSubmit}
        okLoading={loading}
        okText='Add new supplier'
      />
    ),
  };

  return (
    <StyledModal {...modalProps} centered>
      <Form
        form={form}
        className='add-new-supplier-form'
        layout='vertical'
        initialValues={{
          status: 'Active',
          type: 'Sales Contact',
          visibility: 'Public',
          country: 'US',
        }}
        id='add-new-suppliers-edit-form'
      >
        <Fieldset title='New Supplier Creation' style={{ marginBottom: 10 }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                // className='form-cell'
                label='Company Name'
                name='supplierName'
                rules={[
                  { required: true, message: 'Company Name is required' },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                // className='form-cell'
                label='Company Website'
                name='companyWebsite'
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Fieldset>

        <Fieldset title='Sales Contact' style={{ marginBottom: 10 }}>
          <Row gutter={16}>
            {/* Column 1 */}
            <Col span={8}>
              <Form.Item
                className='form-cell'
                label='Type'
                name='type'
                rules={[{ required: true }]}
              >
                <Select disabled />
              </Form.Item>

              <Form.Item
                className='form-cell'
                label='Department'
                name='department'
              >
                <Input placeholder='Please input department' />
              </Form.Item>

              <Form.Item
                className='form-cell'
                label='Phone Number'
                name='saleContactPhone'
                rules={[{ required: true, message: 'Phone is required' }]}
              >
                <Input placeholder='Please input phone number' />
              </Form.Item>

              <Form.Item
                className='form-cell'
                label='State'
                name='state'
                rules={[{ required: true, message: 'State is required' }]}
              >
                <Input placeholder='Please input state' />
              </Form.Item>
              {/* TALL ROW (last in Column 1) */}
              <Form.Item
                className='form-cell form-cell--tall'
                label='Description'
                name='description'
              >
                <Input.TextArea
                  rows={5}
                  placeholder='Please input Description'
                />
              </Form.Item>
            </Col>

            {/* Column 2 */}
            <Col span={8}>
              <Form.Item
                className='form-cell'
                label='First Name'
                name='saleContactFirstName'
                rules={[{ required: true, message: 'First Name is required' }]}
              >
                <Input placeholder='Please input first name' />
              </Form.Item>

              <Form.Item
                className='form-cell'
                label='Primary Email'
                name='saleContactEmail'
                rules={[
                  { required: true, message: 'Primary Email is required' },
                  { type: 'email', message: 'Invalid Email' },
                ]}
              >
                <Input placeholder='Please input email' />
              </Form.Item>

              <Form.Item
                className='form-cell '
                label='Address'
                name='address'
                rules={[{ required: true, message: 'Address is required' }]}
              >
                <Input placeholder='Please input address' />
              </Form.Item>

              <Form.Item
                className='form-cell'
                label='City'
                name='city'
                rules={[{ required: true, message: 'City is required' }]}
              >
                <Input placeholder='Please input city' />
              </Form.Item>
            </Col>

            {/* Column 3 */}
            <Col span={8}>
              <Form.Item
                className='form-cell'
                label='Last Name'
                name='saleContactLastName'
                rules={[{ required: true, message: 'Last Name is required' }]}
              >
                <Input placeholder='Please input last name' />
              </Form.Item>

              <Form.Item
                className='form-cell'
                name='alternativeEmails'
                label={<FormattedMessage {...Messages.labelAlterEmail} />}
              >
                <Input placeholder='Please input Alternate Email' />
              </Form.Item>

              <Form.Item
                className='form-cell'
                label='Country'
                name='country'
                rules={[{ required: true, message: 'Country is required' }]}
              >
                <FlagCountrySelectionFieldInput
                  searchable={true}
                  className='add-new-supplier-form__country-input'
                  placeholder='Please input country'
                  onSelect={(value) => {
                    form.setFieldsValue({
                      ...form.getFieldsValue(),
                      country: value,
                    });
                  }}
                />
              </Form.Item>

              <Form.Item
                className='form-cell'
                label='Zip Code'
                name='zipCode'
                rules={[{ required: true, message: 'Zip Code is required' }]}
              >
                <Input placeholder='Please input zip code' />
              </Form.Item>
            </Col>
          </Row>
        </Fieldset>
      </Form>
    </StyledModal>
  );
};

export default CreateSupplierModal;
